<template>
  <div class="row gutter-x-md items-center">
    <div :class="{'col-12' : isNewSetting && !currentSetting, 'col-sm-6 col-xs-12' : currentSetting}">
      <strong v-if="currentSetting && !isNewSetting">
        {{ capitalize(currentSetting.key).replace(/_/g, ' ') }} <small>(<code>{{ currentSetting.key }}</code>)</small>
      </strong>
      <q-input
        v-else
        v-model="settingQuery"
        float-label="Setting"
        clearable
        @input="settingQueryChange"
      >
        <q-autocomplete
          :min-characters="1"
          @search="settingSearch"
          @selected="settingSelected"
        />
      </q-input>
      <div v-if="currentSetting">
        <p class="no-margin">
          {{ currentSetting.description }}
        </p>
        <p
          v-if="currentSetting.validation_rules"
          class="no-margin text-grey"
        >
          <small>Validation rules: </small>
          <q-chip
            v-for="(rule, ruleIndex) in currentSetting.validation_rules.split('|')"
            :key="ruleIndex"
            :class="ruleIndex ? 'q-ml-sm' : ''"
            small
            dense
          >
            {{ rule }}
          </q-chip>
        </p>
      </div>
    </div>
    <div
      v-if="currentSetting"
      :class="{'col-12' : isNewSetting && !currentSetting, 'col-sm-6 col-xs-12' : currentSetting}"
      class="text-right"
    >
      <div class="row items-center gutter-x-sm">
        <div class="col">
          <q-toggle
            v-if="currentSetting.type === 'toggle'"
            v-model.lazy="currentSetting.value"
            :readonly="!editable"
            @input="update"
          />
          <q-input
            v-else
            v-model.trim.lazy="currentSetting.value"
            inverted-light
            color="grey-2"
            :type="currentSetting.type"
            stack-label="Value"
            :readonly="!editable"
            @input="update"
          />
          <ul
            v-if="currentSetting.children"
            class="text-left"
          >
            <li
              v-for="childrenSetting in currentSetting.children"
              :key="childrenSetting.id"
            >
              <template v-if="childrenSetting.market">
                Market override for {{ childrenSetting.market.name }}: <strong>{{ childrenSetting.value }}</strong>
              </template>
            </li>
          </ul>
        </div>
        <div class="col-auto">
          <q-btn
            v-if="isNewSetting"
            v-tippy
            rounded
            color="primary"
            icon="save"
            outline
            title="Save setting"
            @click="storeSetting"
          />
          <q-btn
            v-if="deletable"
            v-tippy
            rounded
            color="negative"
            icon="delete"
            outline
            title="Delete setting"
            @click="deleteSetting"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'quasar';

export default {
  props: {
    setting: {
      type: Object,
      default: null,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    deletable: {
      type: Boolean,
      default: false,
    },
    market: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      currentSetting: null,
      settingQuery: null,
    };
  },
  computed: {
    isNewSetting() {
      return this.setting === null;
    },
  },
  created() {
    if (!this.isNewSetting) {
      this.currentSetting = this.setting;
    }

    this.update = debounce(() => {
      if (this.isNewSetting) {
        return;
      }

      const payload = { value: this.currentSetting.value };
      this.api().request('PUT', `settings/${this.currentSetting.id}`, payload)
        .then(() => {
          this.uiNotify('Setting saved successfully');
        }).catch((response) => {
          this.uiNotify(this.getFirstError(response) || 'Error occurred', 'negative');
        });
    }, 600);
  },
  methods: {
    settingSearch(terms, done) {
      this.api().request('GET', `settings/${terms}`).then((settings) => {
        const filteredSettings = settings.filter((setting) => this.market.settings.findIndex((marketSetting) => marketSetting.key === setting.key) === -1).map((setting) => {
          const label = this.capitalize(setting.key).replace(/_/g, ' ');
          return {
            label,
            setting,
            value: label,
          };
        });

        done(filteredSettings);
      }).catch(() => {
        this.uiNotify('Error occurred', 'negative');
        done([]);
      });
    },
    settingSelected(item, navigatedWithKeyboard) {
      if (!navigatedWithKeyboard) {
        this.currentSetting = item.setting;
      }
    },
    settingQueryChange(settingQuery) {
      if (settingQuery.length === 0) {
        this.currentSetting = null;
      }
    },
    storeSetting() {
      const payload = {
        key: this.currentSetting.key,
        value: this.currentSetting.value,
        market_id: this.market.id,
      };
      this.api().request('POST', 'settings', payload)
        .then((setting) => {
          this.uiNotify('Setting saved successfully');
          this.$emit('stored', setting);
          this.currentSetting = null;
          this.settingQuery = null;
        }).catch((response) => {
          this.uiNotify(this.getFirstError(response) || 'Error occurred', 'negative');
        });
    },
    deleteSetting() {
      this.uiConfirm('Are you sure?').then(() => {
        this.api().request('DELETE', `settings/${this.currentSetting.id}`)
          .then(() => {
            this.uiNotify('Setting deleted successfully');
            this.$emit('deleted', this.currentSetting);
          }).catch((response) => {
            this.uiNotify(response.message || 'Error occurred', 'negative');
          });
      });
    },
  },
};
</script>
